/**
 * Exchange account schemas
 */
import { z } from "zod";
import { createEntityFilterSchema, PaginationParamsSchema, PublicationStateSchema, StrapiIdSchema, } from "./common.schemas";
import { ExchangeAccountRelationSchema, LazyBotConfigRelation, LazyExchangeRelation, LazyFreePeriodPackageRelation, LazyUserRelation, } from "./relations";
// ============================================================================
// Input Schemas
// ============================================================================
/**
 * Trading credentials for exchange accounts (MetaAPI, etc.)
 */
export const ExchangeAccountCredentialsSchema = z
    .object({
    username: z.string().optional(),
    password: z.string().optional(),
    server: z.string().optional(),
    /** The user kept a server name the broker directory does not list (see validate-server). */
    serverConfirmed: z.boolean().optional(),
})
    .strict();
/**
 * MT5 server name pre-check input/result (POST /exchange-accounts/validate-server)
 */
export const ValidateServerInputSchema = z
    .object({
    server: z.string(),
    login: z.string().optional(),
})
    .strict();
export const ValidateServerResultSchema = z.union([
    z.object({ ok: z.literal(true), server: z.string(), verified: z.boolean() }),
    z.object({
        ok: z.literal(false),
        code: z.literal("INVALID_SERVER"),
        reason: z.enum(["SYNTAX", "NOT_FOUND"]),
        issue: z.string().optional(),
        suggestions: z.array(z.string()),
    }),
]);
/**
 * Exchange account create input schema
 */
export const ExchangeAccountCreateInputSchema = z
    .object({
    exchange: StrapiIdSchema,
    name: z.string().optional(),
    credentials: ExchangeAccountCredentialsSchema.optional(),
    defaultLeverage: z.number().optional(),
})
    .strict();
/**
 * Exchange account update input schema
 * Fields allowed by Strapi's pick-body-properties middleware: name, credentials, hidden, defaultLeverage
 */
export const ExchangeAccountUpdateInputSchema = z
    .object({
    name: z.string().optional(),
    hidden: z.boolean().optional(),
    defaultLeverage: z.number().optional(),
    credentials: ExchangeAccountCredentialsSchema.optional(),
})
    .strict();
/**
 * Exchange account filters schema - typesafe filters for exchange account queries
 * Created using createEntityFilterSchema to reuse field types from ExchangeAccountRelationSchema
 */
export const ExchangeAccountFiltersSchema = createEntityFilterSchema(ExchangeAccountRelationSchema, {
    scalars: ["uuid", "accountId", "name", "status", "hidden"],
    relations: ["user", "exchange"],
});
/**
 * Exchange account populate schema - allowed relations to populate
 */
export const ExchangeAccountPopulateSchema = z.array(z.enum(["user", "exchange", "bot_configs", "free_period_packages", "exchange.servers"]));
/**
 * Exchange account query params schema
 * Uses .strict() to reject unknown parameters
 */
export const ExchangeAccountQueryParamsSchema = z
    .object({
    // Pagination (nested format from frontend)
    pagination: PaginationParamsSchema.optional(),
    // Sort
    sort: z.array(z.string()).optional(),
    // Filters
    filters: ExchangeAccountFiltersSchema.optional(),
    // Populate
    populate: ExchangeAccountPopulateSchema.optional(),
    // Publication state (draft/publish)
    publicationState: PublicationStateSchema.optional(),
})
    .strict();
/**
 * Move exchange account input schema (admin)
 */
export const MoveExchangeAccountInputSchema = z
    .object({
    id: StrapiIdSchema,
    fromUserId: StrapiIdSchema,
    toUserId: StrapiIdSchema,
})
    .strict();
// ============================================================================
// Entity Schemas
// ============================================================================
/**
 * Exchange account schema - extends ExchangeAccountRelationSchema with relations
 */
export const ExchangeAccountSchema = ExchangeAccountRelationSchema.extend({
    // Relations (populated) - using lazy schemas to break circular deps
    exchange: LazyExchangeRelation.nullish(),
    user: LazyUserRelation.nullish(),
    bot_configs: z.array(LazyBotConfigRelation).nullish(),
    free_period_packages: z.array(LazyFreePeriodPackageRelation).nullish(),
});
